/**
 * AgentPerformanceAnalytics - MA-ARCH-152
 * 
 * Unified agent performance dashboard for War Room.
 * Combines booking and sales agent metrics with segment analysis.
 * 
 * Core Questions Answered:
 * - Who performs best?
 * - Under what conditions (script, profit path, funnel)?
 * - Where do performance differences appear in the pipeline?
 * 
 * Features:
 * - Leaderboard with ranked table
 * - Time period selector
 * - Filtering by agent, script, funnel, vertical, profit path
 * - Individual agent drilldown
 * - Benchmark comparison
 * - Trend sparklines (if data available)
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Users,
  Trophy,
  TrendingUp,
  TrendingDown,
  Target,
  DollarSign,
  Clock,
  Calendar,
  FileText,
  GitBranch,
  Filter,
  Search,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Minus,
  Loader2,
  User,
  BarChart3,
  Zap,
  Phone,
  ClipboardCheck,
  Award,
  Medal,
  Star,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';
import PerformanceInsights from './PerformanceInsights';

// =============================================================================
// CONFIGURATION
// =============================================================================

const roleConfig = {
  booking: { label: 'Booking', color: 'text-blue-400', bgClass: 'bg-blue-500/20 border-blue-500/30' },
  sales: { label: 'Sales', color: 'text-emerald-400', bgClass: 'bg-emerald-500/20 border-emerald-500/30' },
  hybrid: { label: 'Hybrid', color: 'text-violet-400', bgClass: 'bg-violet-500/20 border-violet-500/30' },
  unknown: { label: 'Unknown', color: 'text-slate-400', bgClass: 'bg-slate-500/20 border-slate-500/30' },
};

const benchmarkStatus = (variance) => {
  if (variance === undefined || variance === null) return { color: 'text-slate-400', icon: Minus };
  if (variance >= 0) return { color: 'text-emerald-400', icon: TrendingUp };
  if (variance >= -10) return { color: 'text-amber-400', icon: TrendingDown };
  return { color: 'text-red-400', icon: TrendingDown };
};

// =============================================================================
// FORMATTERS
// =============================================================================

const pct = (val) => {
  if (val === null || val === undefined) return '—';
  return `${(val * 100).toFixed(1)}%`;
};

const currency = (val) => {
  if (val === null || val === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
};

const formatHours = (hours) => {
  if (hours === null || hours === undefined) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

// =============================================================================
// API
// =============================================================================

async function fetchAgentPerformance(params) {
  const queryParams = new URLSearchParams();
  queryParams.append('period', params.period || 'mtd');
  if (params.agent_role) queryParams.append('agent_role', params.agent_role);
  if (params.script_id) queryParams.append('script_id', params.script_id);
  if (params.profit_path_id) queryParams.append('profit_path_id', params.profit_path_id);
  if (params.funnel_id) queryParams.append('funnel_id', params.funnel_id);
  if (params.vertical) queryParams.append('vertical', params.vertical);
  if (params.campaign_id) queryParams.append('campaign_id', params.campaign_id);
  if (params.tenant_id) queryParams.append('tenant_id', params.tenant_id);
  
  const response = await api.get(`/analytics/agent-performance?${queryParams}`);
  return response.data;
}

async function fetchAgentDetail(agentId, params) {
  const queryParams = new URLSearchParams();
  queryParams.append('period', params.period || 'mtd');
  if (params.tenant_id) queryParams.append('tenant_id', params.tenant_id);
  
  const response = await api.get(`/analytics/agent-performance/${agentId}?${queryParams}`);
  return response.data;
}

async function fetchScripts(tenantId) {
  const params = tenantId ? `?tenant_id=${tenantId}` : '';
  const response = await api.get(`/scripts${params}`);
  return response.data;
}

async function fetchProfitPaths(tenantId) {
  const params = tenantId ? `?tenant_id=${tenantId}` : '';
  const response = await api.get(`/profit-paths${params}`);
  return response.data?.profit_paths || [];
}

// =============================================================================
// SUMMARY CARDS
// =============================================================================

function SummaryCards({ data }) {
  const cards = [
    {
      title: 'Total Agents',
      value: data?.total_agents || 0,
      icon: Users,
      color: 'text-indigo-400',
    },
    {
      title: 'Total Leads',
      value: data?.total_leads || 0,
      icon: Target,
      color: 'text-blue-400',
    },
    {
      title: 'Total Appointments',
      value: data?.total_appointments || 0,
      icon: Calendar,
      color: 'text-violet-400',
    },
    {
      title: 'Total Revenue',
      value: currency(data?.total_revenue || 0),
      icon: DollarSign,
      color: 'text-emerald-400',
    },
  ];
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {cards.map((card) => (
        <Card key={card.title} className="glass border-white/10">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <card.icon className={cn("h-4 w-4", card.color)} />
              <span className="text-xs text-slate-400">{card.title}</span>
            </div>
            <p className={cn("text-lg font-semibold mt-1", card.color)}>
              {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// =============================================================================
// LEADERBOARD ROW
// =============================================================================

function LeaderboardRow({ agent, rank, benchmarks, onSelect }) {
  const roleConf = roleConfig[agent.agent_role] || roleConfig.unknown;
  const revenueVariance = agent.vs_benchmark?.lead_to_appointment;
  const varStatus = benchmarkStatus(revenueVariance);
  const VarIcon = varStatus.icon;
  
  const RankIcon = rank === 1 ? Trophy : rank === 2 ? Medal : rank === 3 ? Award : null;
  const rankColor = rank === 1 ? 'text-amber-400' : rank === 2 ? 'text-slate-300' : rank === 3 ? 'text-amber-600' : 'text-slate-500';
  
  return (
    <TableRow 
      className="border-white/10 hover:bg-white/[0.02] cursor-pointer"
      onClick={() => onSelect(agent)}
      data-testid={`agent-row-${agent.agent_id}`}
    >
      {/* Rank */}
      <TableCell className="w-12">
        <div className={cn("flex items-center justify-center", rankColor)}>
          {RankIcon ? <RankIcon className="h-4 w-4" /> : <span className="text-sm">{rank}</span>}
        </div>
      </TableCell>
      
      {/* Agent */}
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
            <User className="h-4 w-4 text-slate-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-100">{agent.agent_name}</p>
            <Badge className={cn("text-[10px] border", roleConf.bgClass)}>
              {roleConf.label}
            </Badge>
          </div>
        </div>
      </TableCell>
      
      {/* Volume */}
      <TableCell className="text-right">
        <div className="text-sm text-slate-300">{agent.leads_handled}</div>
        <div className="text-[10px] text-slate-500">{agent.appointments_handled} appts</div>
      </TableCell>
      
      {/* Conversion */}
      <TableCell className="text-right">
        <div className="text-sm text-slate-300">{pct(agent.lead_to_appointment_rate)}</div>
        <div className="text-[10px] text-slate-500">L→A</div>
      </TableCell>
      
      {/* Show Rate */}
      <TableCell className="text-right">
        <div className="text-sm text-slate-300">{pct(agent.show_rate)}</div>
        <div className="text-[10px] text-slate-500">Show</div>
      </TableCell>
      
      {/* Close Rate */}
      <TableCell className="text-right">
        <div className="text-sm text-slate-300">{pct(agent.close_rate)}</div>
        <div className="text-[10px] text-slate-500">Close</div>
      </TableCell>
      
      {/* Revenue/Lead */}
      <TableCell className="text-right">
        <div className="text-sm font-medium text-emerald-400">{currency(agent.revenue_per_lead)}</div>
        <div className="text-[10px] text-slate-500">per lead</div>
      </TableCell>
      
      {/* vs Benchmark */}
      <TableCell className="text-right">
        {revenueVariance !== undefined ? (
          <div className={cn("flex items-center justify-end gap-1", varStatus.color)}>
            <VarIcon className="h-3 w-3" />
            <span className="text-xs">{revenueVariance > 0 ? '+' : ''}{revenueVariance}%</span>
          </div>
        ) : (
          <span className="text-slate-500 text-xs">—</span>
        )}
      </TableCell>
      
      {/* Quality */}
      <TableCell className="text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <div className="flex items-center justify-end gap-1">
                <ClipboardCheck className={cn(
                  "h-3 w-3",
                  agent.data_capture_completeness >= 0.8 ? "text-emerald-400" :
                  agent.data_capture_completeness >= 0.5 ? "text-amber-400" : "text-red-400"
                )} />
                <span className="text-xs text-slate-300">{pct(agent.data_capture_completeness)}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Data Capture: {pct(agent.data_capture_completeness)}</p>
              <p className="text-xs">Script Adherence: {pct(agent.script_adherence_score)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      
      {/* Flags */}
      <TableCell>
        {agent.performance_flags?.length > 0 ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <AlertTriangle className="h-4 w-4 text-amber-400" />
              </TooltipTrigger>
              <TooltipContent>
                {agent.performance_flags.map((flag, i) => (
                  <p key={i} className="text-xs">{flag.replace(/_/g, ' ')}</p>
                ))}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <CheckCircle className="h-4 w-4 text-emerald-400" />
        )}
      </TableCell>
      
      <TableCell>
        <ChevronRight className="h-4 w-4 text-slate-500" />
      </TableCell>
    </TableRow>
  );
}

// =============================================================================
// AGENT DETAIL DIALOG
// =============================================================================

function AgentDetailDialog({ agent, period, tenantId, open, onClose }) {
  const { data: detail, isLoading } = useQuery({
    queryKey: ['agent-detail', agent?.agent_id, period, tenantId],
    queryFn: () => fetchAgentDetail(agent.agent_id, { period, tenant_id: tenantId }),
    enabled: !!agent && open,
  });
  
  if (!agent) return null;
  
  const roleConf = roleConfig[agent.agent_role] || roleConfig.unknown;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
              <User className="h-5 w-5 text-slate-400" />
            </div>
            <div>
              <span className="text-slate-100">{agent.agent_name}</span>
              <Badge className={cn("ml-2 text-xs border", roleConf.bgClass)}>
                {roleConf.label}
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Performance Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                <p className="text-xs text-slate-500 uppercase">Revenue/Lead</p>
                <p className="text-lg font-semibold text-emerald-400">{currency(agent.revenue_per_lead)}</p>
              </div>
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                <p className="text-xs text-slate-500 uppercase">Booking Rate</p>
                <p className="text-lg font-semibold text-blue-400">{pct(agent.lead_to_appointment_rate)}</p>
              </div>
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                <p className="text-xs text-slate-500 uppercase">Close Rate</p>
                <p className="text-lg font-semibold text-violet-400">{pct(agent.close_rate)}</p>
              </div>
            </div>
            
            {/* Volume Breakdown */}
            <div>
              <h4 className="text-sm font-medium text-slate-200 mb-2">Volume Metrics</h4>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="p-2 rounded bg-white/[0.02]">
                  <p className="text-lg font-semibold text-slate-100">{agent.leads_handled}</p>
                  <p className="text-[10px] text-slate-500">Leads</p>
                </div>
                <div className="p-2 rounded bg-white/[0.02]">
                  <p className="text-lg font-semibold text-slate-100">{agent.appointments_set}</p>
                  <p className="text-[10px] text-slate-500">Appts Set</p>
                </div>
                <div className="p-2 rounded bg-white/[0.02]">
                  <p className="text-lg font-semibold text-slate-100">{agent.appointments_handled}</p>
                  <p className="text-[10px] text-slate-500">Appts Held</p>
                </div>
                <div className="p-2 rounded bg-white/[0.02]">
                  <p className="text-lg font-semibold text-slate-100">{agent.clients_closed}</p>
                  <p className="text-[10px] text-slate-500">Clients</p>
                </div>
              </div>
            </div>
            
            {/* Quality Metrics */}
            <div>
              <h4 className="text-sm font-medium text-slate-200 mb-2">Quality Metrics</h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded bg-white/[0.02]">
                  <p className="text-xs text-slate-500">Data Capture</p>
                  <p className={cn(
                    "text-sm font-medium",
                    agent.data_capture_completeness >= 0.8 ? "text-emerald-400" :
                    agent.data_capture_completeness >= 0.5 ? "text-amber-400" : "text-red-400"
                  )}>{pct(agent.data_capture_completeness)}</p>
                </div>
                <div className="p-2 rounded bg-white/[0.02]">
                  <p className="text-xs text-slate-500">Script Adherence</p>
                  <p className="text-sm font-medium text-slate-300">{pct(agent.script_adherence_score)}</p>
                </div>
                <div className="p-2 rounded bg-white/[0.02]">
                  <p className="text-xs text-slate-500">Scripts Run</p>
                  <p className="text-sm font-medium text-slate-300">{agent.scripts_run}</p>
                </div>
              </div>
            </div>
            
            {/* Speed Metrics */}
            {(agent.avg_time_to_contact_hours || agent.avg_sales_cycle_days) && (
              <div>
                <h4 className="text-sm font-medium text-slate-200 mb-2">Speed Metrics</h4>
                <div className="grid grid-cols-2 gap-2">
                  {agent.avg_time_to_contact_hours && (
                    <div className="p-2 rounded bg-white/[0.02]">
                      <p className="text-xs text-slate-500">Time to Contact</p>
                      <p className="text-sm font-medium text-slate-300">{formatHours(agent.avg_time_to_contact_hours)}</p>
                    </div>
                  )}
                  {agent.avg_sales_cycle_days && (
                    <div className="p-2 rounded bg-white/[0.02]">
                      <p className="text-xs text-slate-500">Sales Cycle</p>
                      <p className="text-sm font-medium text-slate-300">{agent.avg_sales_cycle_days} days</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Top Scripts */}
            {agent.top_scripts?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-200 mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-400" />
                  Top Scripts
                </h4>
                <div className="space-y-1">
                  {agent.top_scripts.map((s, i) => (
                    <div key={s.script_id} className="flex items-center justify-between p-2 rounded bg-white/[0.02]">
                      <span className="text-sm text-slate-300">{s.script_name}</span>
                      <span className="text-xs text-slate-500">{s.leads} leads, {s.appointments} appts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Top Profit Paths */}
            {agent.top_profit_paths?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-200 mb-2 flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-emerald-400" />
                  Top Profit Paths
                </h4>
                <div className="space-y-1">
                  {agent.top_profit_paths.map((pp, i) => (
                    <div key={pp.profit_path_id} className="flex items-center justify-between p-2 rounded bg-white/[0.02]">
                      <span className="text-sm text-slate-300">{pp.profit_path_name}</span>
                      <span className="text-xs text-slate-500">{pp.leads} leads, {pp.appointments} appts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Performance Flags */}
            {agent.performance_flags?.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <h4 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Performance Alerts
                </h4>
                <ul className="space-y-1">
                  {agent.performance_flags.map((flag, i) => (
                    <li key={i} className="text-xs text-amber-300">• {flag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// SEGMENT ANALYSIS CARD
// =============================================================================

function SegmentAnalysisCard({ title, icon: Icon, data, idKey, nameKey }) {
  if (!data || data.length === 0) return null;
  
  return (
    <Card className="glass border-white/10">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-400" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {data.slice(0, 5).map((item, i) => (
            <div key={item[idKey]} className="flex items-center justify-between">
              <span className="text-sm text-slate-300 truncate max-w-[60%]">{item[nameKey]}</span>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-slate-400">{item.leads} leads</span>
                <span className="text-slate-400">{item.appointments} appts</span>
                <Badge className="text-[10px] bg-slate-800">{item.unique_agents} agents</Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function AgentPerformanceAnalytics({
  period = 'mtd',
  campaignId,
  vertical,
  channel,
}) {
  const { selectedTenantId } = useTenant();
  
  // Filters
  const [roleFilter, setRoleFilter] = useState('all');
  const [scriptFilter, setScriptFilter] = useState('all');
  const [profitPathFilter, setProfitPathFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Detail dialog
  const [selectedAgent, setSelectedAgent] = useState(null);
  
  // Fetch data
  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-performance', period, roleFilter, scriptFilter, profitPathFilter, campaignId, vertical, selectedTenantId],
    queryFn: () => fetchAgentPerformance({
      period,
      agent_role: roleFilter !== 'all' ? roleFilter : undefined,
      script_id: scriptFilter !== 'all' ? scriptFilter : undefined,
      profit_path_id: profitPathFilter !== 'all' ? profitPathFilter : undefined,
      campaign_id: campaignId,
      vertical,
      tenant_id: selectedTenantId,
    }),
    staleTime: 60000,
  });
  
  // Fetch filter options
  const { data: scripts } = useQuery({
    queryKey: ['scripts-list', selectedTenantId],
    queryFn: () => fetchScripts(selectedTenantId),
    staleTime: 300000,
  });
  
  const { data: profitPaths } = useQuery({
    queryKey: ['profit-paths-list', selectedTenantId],
    queryFn: () => fetchProfitPaths(selectedTenantId),
    staleTime: 300000,
  });
  
  // Filter agents by search
  const filteredAgents = useMemo(() => {
    if (!data?.agents) return [];
    if (!searchQuery) return data.agents;
    
    const q = searchQuery.toLowerCase();
    return data.agents.filter(a => 
      a.agent_name?.toLowerCase().includes(q) ||
      a.agent_id?.toLowerCase().includes(q)
    );
  }, [data?.agents, searchQuery]);
  
  if (error) {
    // MA-PHASE11A-WARROOM-FINAL-BLOCKERS-020: Neutral empty state (not red failure)
    return (
      <Card className="glass border-white/10">
        <CardContent className="py-12 text-center">
          <Users className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-slate-300 mb-2">Agent Performance Unavailable</h4>
          <p className="text-slate-500 max-w-md mx-auto">
            Unable to load agent performance data at this time. This may be due to insufficient data for the selected period or filters.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="agent-performance-analytics">
      {/* Header & Filters */}
      <Card className="glass border-white/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-400" />
                Agent Performance Analytics
              </CardTitle>
              <CardDescription className="text-slate-400">
                Who performs best? Under what conditions?
              </CardDescription>
            </div>
          </div>
          
          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 w-48 bg-white/5 border-white/10 text-sm"
                data-testid="agent-search-input"
              />
            </div>
            
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-32 h-8 text-xs bg-white/5 border-white/10">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="booking">Booking</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={scriptFilter} onValueChange={setScriptFilter}>
              <SelectTrigger className="w-40 h-8 text-xs bg-white/5 border-white/10">
                <SelectValue placeholder="Script" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Scripts</SelectItem>
                {scripts?.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={profitPathFilter} onValueChange={setProfitPathFilter}>
              <SelectTrigger className="w-40 h-8 text-xs bg-white/5 border-white/10">
                <SelectValue placeholder="Profit Path" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Profit Paths</SelectItem>
                {profitPaths?.map(pp => (
                  <SelectItem key={pp.id} value={pp.id}>{pp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>
      
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <SummaryCards data={data} />
          
          {/* Leaderboard */}
          <Card className="glass border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-400" />
                Agent Leaderboard
                <Badge className="text-[10px] ml-2 bg-slate-800">
                  {filteredAgents.length} agents
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {filteredAgents.length === 0 ? (
                <div className="py-8 text-center text-slate-400">
                  No agents found for the selected filters
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="w-12 text-slate-300">#</TableHead>
                      <TableHead className="text-slate-300">Agent</TableHead>
                      <TableHead className="text-slate-300 text-right">Volume</TableHead>
                      <TableHead className="text-slate-300 text-right">L→A</TableHead>
                      <TableHead className="text-slate-300 text-right">Show</TableHead>
                      <TableHead className="text-slate-300 text-right">Close</TableHead>
                      <TableHead className="text-slate-300 text-right">Rev/Lead</TableHead>
                      <TableHead className="text-slate-300 text-right">vs Avg</TableHead>
                      <TableHead className="text-slate-300 text-right">Quality</TableHead>
                      <TableHead className="text-slate-300 w-10"></TableHead>
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAgents.map((agent, index) => (
                      <LeaderboardRow
                        key={agent.agent_id}
                        agent={agent}
                        rank={index + 1}
                        benchmarks={data?.benchmarks}
                        onSelect={setSelectedAgent}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          
          {/* Segment Analysis */}
          <div className="grid md:grid-cols-2 gap-4">
            <SegmentAnalysisCard
              title="Performance by Script"
              icon={FileText}
              data={data?.by_script}
              idKey="script_id"
              nameKey="script_name"
            />
            <SegmentAnalysisCard
              title="Performance by Profit Path"
              icon={GitBranch}
              data={data?.by_profit_path}
              idKey="profit_path_id"
              nameKey="profit_path_name"
            />
          </div>
          
          {/* Performance Insights Section */}
          <PerformanceInsights period={period} />
        </>
      )}
      
      {/* Agent Detail Dialog */}
      <AgentDetailDialog
        agent={selectedAgent}
        period={period}
        tenantId={selectedTenantId}
        open={!!selectedAgent}
        onClose={() => setSelectedAgent(null)}
      />
    </div>
  );
}
